import { useState } from 'react';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { checkDateOrder, checkRequired, checkSlug } from '../../utils/validate.js';

// Same rule as slugFromInput() in packages/core: people often paste their website here
// ("https://www.techsummit.events") — drop the scheme, www. and any path, dots → hyphens.
const slugify = (input) => String(input || '').trim().toLowerCase()
  .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
  .replace(/^www\d*\./, '')
  .replace(/[/?#].*$/, '')
  .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 50)
  .replace(/-+$/, '');

/** While typing: a pasted URL is cleaned at once; otherwise dots, spaces etc. become
 *  hyphens as you go (a trailing hyphen is kept so typing "big-" isn't fought). */
const typingSlug = (v) => (/:\/\/|^www\d*\./i.test(v) ? slugify(v) : v.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+/, '').slice(0, 50));

/**
 * Create an event, or edit its name, dates and brand. The web address (slug) is set
 * once: it gets printed in emails, so it can't change afterwards.
 * brands: the company's active brands ([] = the company has none, no picker shown).
 */
export default function EventModal({ event, brands, onClose, onSaved }) {
  const editing = !!event;
  const [form, setForm] = useState({
    name: event?.name || '',
    slug: event?.slug || '',
    starts_on: event?.starts_on || '',
    ends_on: event?.ends_on || '',
    brand_id: event?.brand?.id ? String(event.brand.id) : '',
  });
  const [slugTouched, setSlugTouched] = useState(editing);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const setName = (name) => setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }));

  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      name: checkRequired(form.name, 'the event’s name', { max: 200 }),
      slug: editing ? '' : checkSlug(form.slug),
      ends_on: checkDateOrder(form.starts_on, form.ends_on),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    const body = { name: form.name, starts_on: form.starts_on || null, ends_on: form.ends_on || null, brand_id: form.brand_id ? Number(form.brand_id) : null };
    try {
      const res = editing
        ? await api(`/portal/events/${event.id}`, { method: 'PATCH', body })
        : await api('/portal/events', { method: 'POST', body: { ...body, slug: form.slug } });
      onSaved(res.event);
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? 'Edit event details' : 'New event'}
      description={editing ? undefined : 'You can add its official channels (website, email, phone, hotel) on the next page. The dates decide when its own channels count.'}
      onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel={editing ? 'Save' : 'Create event'} busyLabel={editing ? 'Saving…' : 'Creating…'} busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Event name" required value={form.name} onChange={(e) => { setName(e.target.value); if (errors.name) setErrors({ ...errors, name: undefined }); }} error={errors.name}
        placeholder="TechSummit 2027" maxLength={200} />
      {editing ? (
        <div className="readonly-value">
          <span className="label-small">Web address</span>
          <span className="mono">summitshield.io/{event.slug}</span>
        </div>
      ) : (
        <Field label="Web address" required value={form.slug} error={errors.slug} spellCheck={false} autoComplete="off"
          onChange={(e) => { setSlugTouched(true); set('slug', typingSlug(e.target.value)); }}
          onBlur={(e) => {
            const slug = slugify(e.target.value);
            set('slug', slug);
            if (slug) setErrors((x) => ({ ...x, slug: checkSlug(slug) || undefined }));
          }}
          maxLength={50} autoCapitalize="none"
          hint={!errors.slug ? `Your verify page will be summitshield.io/${form.slug || 'your-event'}. It can’t be changed later, because it gets printed in your emails. Use 3–50 lower-case letters, numbers and hyphens.` : undefined} />
      )}
      {brands.length > 0 && (
        <Field label="Brand" as="select" value={form.brand_id} onChange={(e) => set('brand_id', e.target.value)} error={errors.brand_id}
          hint="The event uses the brand’s channels as well as the company’s.">
          <option value="">No brand — the company itself</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Field>
      )}
      <div className="row-2">
        <Field label="Starts" optional type="date" value={form.starts_on} error={errors.starts_on}
          onChange={(e) => { set('starts_on', e.target.value); setErrors((x) => ({ ...x, ends_on: undefined })); }} />
        <Field label="Ends" optional type="date" value={form.ends_on} min={form.starts_on || undefined} error={errors.ends_on}
          onChange={(e) => set('ends_on', e.target.value)}
          onBlur={() => setErrors((x) => ({ ...x, ends_on: checkDateOrder(form.starts_on, form.ends_on) || undefined }))} />
      </div>
    </Modal>
  );
}
